// src/context/ClinicaContext.js
// =========================================================================
// ESPECIALIDADES DE LA CLÍNICA EN EL FRONTEND
//
// Carga una vez (tras iniciar sesión) la configuración de la clínica: las
// especialidades de su personal (cada cita usa la de su profesional), su plantilla de ficha clínica, su vocabulario y el
// tarifario. Las pantallas lo consumen con useClinica():
//
//   const { termino, definicion, servicios, principal } = useClinica();
//   termino('consultas')          → 'Sesiones' en fisioterapia
//   definicion('odontologia')     → plantilla de la ficha dental
//
// Mientras carga (o si falla) se comporta como medicina general, que es el
// comportamiento de siempre de la aplicación.
// =========================================================================
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import api from '../services/api';
import { useAuth } from './AuthContext';

const ESPECIALIDAD_POR_DEFECTO = 'medicina_general';

const TERMINOS_POR_DEFECTO = {
    consulta: 'Consulta', consultas: 'Consultas',
    profesional: 'Médico', profesionales: 'Médicos',
    paciente: 'Paciente', pacientes: 'Pacientes'
};

const ClinicaContext = createContext(null);

export const ClinicaProvider = ({ children }) => {
    const { user } = useAuth();
    const [config, setConfig] = useState(null);
    const [cargando, setCargando] = useState(false);

    const recargar = useCallback(async () => {
        setCargando(true);
        try {
            // El interceptor de api ya devuelve el cuerpo de la respuesta
            const res = await api.get('/clinica/configuracion');
            setConfig(res);
            return res;
        } catch {
            // Sin configuración la app sigue funcionando como medicina general
            setConfig(null);
            return null;
        } finally {
            setCargando(false);
        }
    }, []);

    useEffect(() => {
        if (user) recargar();
        else setConfig(null);
    }, [user, recargar]);

    const valor = useMemo(() => {
        const especialidades = config?.especialidades || [ESPECIALIDAD_POR_DEFECTO];
        const principal = config?.principal || ESPECIALIDAD_POR_DEFECTO;
        const definiciones = config?.definiciones || {};

        const definicion = (id) => definiciones[id || principal] || definiciones[principal] || null;

        // Vocabulario: el de la especialidad principal. En una clínica
        // multiespecialidad se usa un término neutro si las especialidades
        // no coinciden (p. ej. «Sesiones» y «Consultas» → «Citas»).
        const termino = (clave) => {
            const valores = new Set(especialidades
                .map(id => definiciones[id]?.terminologia?.[clave])
                .filter(Boolean));
            if (valores.size > 1) {
                if (clave === 'consulta') return 'Cita';
                if (clave === 'consultas') return 'Citas';
                if (clave === 'profesional') return 'Profesional';
                if (clave === 'profesionales') return 'Profesionales';
            }
            return definiciones[principal]?.terminologia?.[clave] || TERMINOS_POR_DEFECTO[clave] || clave;
        };

        // Ficha que usa un profesional según su especialidad ('logopedia' → medicina general)
        const plantillaDe = (especialidadProfesional) =>
            config?.plantillas?.[especialidadProfesional] || config?.plantillaPorDefecto || ESPECIALIDAD_POR_DEFECTO;

        return {
            cargando,
            configurada: Boolean(config?.configurada),
            especialidades,
            principal,
            definiciones,
            servicios: config?.servicios || [],
            bonos: config?.bonos || [],
            odontogramaCatalogo: config?.odontograma || null,
            definicion,
            termino,
            plantillaDe,
            recargar,
            actualizarLocal: setConfig
        };
    }, [config, cargando, recargar]);

    return <ClinicaContext.Provider value={valor}>{children}</ClinicaContext.Provider>;
};

export const useClinica = () => {
    const contexto = useContext(ClinicaContext);
    if (!contexto) throw new Error('useClinica debe usarse dentro de <ClinicaProvider>');
    return contexto;
};
